/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Close, FlagOutlined } from '@mui/icons-material'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  UltraCreateFlagReasonOptionMutation,
  UltraCreateFlagReasonOptionMutationVariables,
  UltraFlaggedTypeChoices,
  UltraSearchFlagReasonOptionsQuery,
  UltraSearchFlagReasonOptionsQueryVariables,
} from '@dai/graphql'
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'

import {
  CREATE_FLAG_REASON_OPTION,
  SEARCH_FLAG_REASON_OPTIONS,
} from '@/graphql'
import useT from '@/i18n/useT'

type CreateReasonOption = {
  id: string
  reason: string
  type: UltraFlaggedTypeChoices
  create: true
}

type ReasonOptionValue =
  | UltraSearchFlagReasonOptionsQuery['searchFlagReasonOptions'][number]
  | CreateReasonOption

type FlagItemModalProps = {
  open: boolean
  onClose: () => void
  onSubmit: (input: {
    reasonOptionId: string
    moreInfo?: string
  }) => Promise<void>
  flagType: UltraFlaggedTypeChoices
  title: string
  subjectLabel: string
}

const isCreateOption = (
  option: ReasonOptionValue | null,
): option is CreateReasonOption => Boolean(option && 'create' in option)

const normalizeReason = (value: string) => value.trim().toLowerCase()
const toTitleCase = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value

function FlagItemModal({
  open,
  onClose,
  onSubmit,
  flagType,
  title,
  subjectLabel,
}: FlagItemModalProps) {
  const { t } = useT()
  const titleCaseSubjectLabel = React.useMemo(
    () => toTitleCase(subjectLabel),
    [subjectLabel],
  )
  const [inputValue, setInputValue] = React.useState('')
  const [selectedOption, setSelectedOption] =
    React.useState<ReasonOptionValue | null>(null)
  const [moreInfo, setMoreInfo] = React.useState('')
  const [submitError, setSubmitError] = React.useState('')

  const [searchReasons, { data, loading: searching }] = useLazyQuery<
    UltraSearchFlagReasonOptionsQuery,
    UltraSearchFlagReasonOptionsQueryVariables
  >(SEARCH_FLAG_REASON_OPTIONS, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
  })

  const [createReasonOption, { loading: creatingReason }] = useMutation<
    UltraCreateFlagReasonOptionMutation,
    UltraCreateFlagReasonOptionMutationVariables
  >(CREATE_FLAG_REASON_OPTION, {
    context: { endPoint: 'mlUltra' },
  })

  React.useEffect(() => {
    if (!open) return
    const query = inputValue.trim()
    const timeoutId = window.setTimeout(() => {
      void searchReasons({
        variables: {
          type: flagType,
          query,
        },
      })
    }, 250)

    return () => window.clearTimeout(timeoutId)
  }, [flagType, inputValue, open, searchReasons])

  React.useEffect(() => {
    if (open) return
    setInputValue('')
    setSelectedOption(null)
    setMoreInfo('')
    setSubmitError('')
  }, [open])

  const options = React.useMemo(() => {
    const loadedOptions = data?.searchFlagReasonOptions ?? []
    const sortedOptions = [...loadedOptions].sort((left, right) => {
      const leftPriority = left.type === flagType ? 0 : 1
      const rightPriority = right.type === flagType ? 0 : 1

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority
      }

      return left.reason.localeCompare(right.reason)
    })
    const trimmedInput = inputValue.trim()
    const hasExactMatch = sortedOptions.some(
      option =>
        normalizeReason(option.reason) === normalizeReason(trimmedInput),
    )

    if (!trimmedInput || hasExactMatch) {
      return sortedOptions
    }

    return [
      ...sortedOptions,
      {
        id: `create-${trimmedInput}`,
        type: flagType,
        reason: trimmedInput,
        create: true as const,
      },
    ]
  }, [data?.searchFlagReasonOptions, flagType, inputValue])

  const isSubmitting = creatingReason
  const selectedReason = selectedOption?.reason ?? inputValue.trim()
  const canSubmit = Boolean(selectedReason) && !isSubmitting
  const specificReasonLabel = t('flagItemModal.reason.specificLabel', {
    subjectLabel: titleCaseSubjectLabel,
    defaultValue: `${titleCaseSubjectLabel} reason`,
  })
  const commonReasonLabel = t(
    'flagItemModal.reason.commonLabel',
    'Common reason',
  )
  const newReasonLabel = t(
    'flagItemModal.reason.newReusable',
    'New reusable reason',
  )

  const handleSubmit = async () => {
    if (!canSubmit) return

    setSubmitError('')

    try {
      let reasonOptionId =
        selectedOption && !isCreateOption(selectedOption)
          ? selectedOption.id
          : null

      if (!reasonOptionId) {
        const reason = (selectedOption?.reason ?? inputValue).trim()
        if (!reason) return
        const existingOption =
          data?.searchFlagReasonOptions.find(
            option =>
              normalizeReason(option.reason) === normalizeReason(reason),
          ) ?? null
        if (existingOption) {
          reasonOptionId = existingOption.id
        }
      }

      if (!reasonOptionId) {
        const reason = (selectedOption?.reason ?? inputValue).trim()
        const response = await createReasonOption({
          variables: {
            input: {
              type: flagType,
              reason,
            },
          },
        })
        reasonOptionId = response.data?.createFlagReasonOption.option.id ?? null
      }

      if (!reasonOptionId) {
        setSubmitError(
          t(
            'flagItemModal.errors.createReason',
            'Failed to create flag reason',
          ),
        )
        return
      }

      await onSubmit({
        reasonOptionId,
        moreInfo: moreInfo.trim() || undefined,
      })
    } catch (error) {
      console.error('Failed to submit flag request', error)
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : t('flagItemModal.errors.submitFlag', 'Failed to submit flag'),
      )
    }
  }

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          aria-label={t('flagItemModal.close.ariaLabel', 'Close flag dialog')}
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('flagItemModal.description', {
              subjectLabel,
              defaultValue: `Choose or create a reusable reason for this ${subjectLabel}.`,
            })}
          </Typography>
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          <Autocomplete<ReasonOptionValue, false, false, false>
            value={selectedOption}
            options={options}
            loading={searching}
            filterOptions={x => x}
            onChange={(_, value) => {
              setSelectedOption(value)
              if (value) {
                setInputValue(value.reason)
              }
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'reset') return
              setInputValue(value)
              setSubmitError('')
              if (
                selectedOption &&
                normalizeReason(selectedOption.reason) !==
                  normalizeReason(value)
              ) {
                setSelectedOption(null)
              }
            }}
            getOptionLabel={option => option.reason}
            isOptionEqualToValue={(option, value) =>
              option.id === value.id && option.reason === value.reason
            }
            groupBy={option =>
              isCreateOption(option)
                ? newReasonLabel
                : option.type === flagType
                  ? specificReasonLabel
                  : commonReasonLabel
            }
            renderGroup={params => (
              <li key={params.key}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 2, pt: 1.25, pb: 0.5, display: 'block' }}
                >
                  {params.group}
                </Typography>
                <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
              </li>
            )}
            renderInput={params => (
              <TextField
                {...params}
                autoFocus
                label={t('flagItemModal.reason.label', 'Reason')}
                placeholder={t(
                  'flagItemModal.reason.placeholder',
                  'Search reasons or type a new one',
                )}
                helperText={t(
                  'flagItemModal.reason.helperText',
                  "We'll create a reusable reason if you enter a new one.",
                )}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack spacing={0.25}>
                  <Typography variant="body2">
                    {isCreateOption(option)
                      ? t('flagItemModal.reason.createOption', {
                          reason: option.reason,
                          defaultValue: `Create "${option.reason}"`,
                        })
                      : option.reason}
                  </Typography>
                </Stack>
              </li>
            )}
          />
          <TextField
            label={t('flagItemModal.notes.label', 'Notes')}
            placeholder={t('flagItemModal.notes.placeholder', 'Optional note')}
            value={moreInfo}
            onChange={event => setMoreInfo(event.target.value)}
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<FlagOutlined />}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {t('common.flag', 'Flag')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FlagItemModal
